<script setup lang="ts">
import type { Tone } from './types'

/**
 * A bare state indicator for table cells and nav items where a full badge would
 * be too heavy. The label is always rendered for assistive technology — a dot
 * alone fails WCAG 1.4.1 (use of colour) — and optionally shown as text.
 */

withDefaults(
  defineProps<{
    tone?: Tone
    /** Required: this is the accessible name of the state. */
    label: string
    /** Show the label next to the dot rather than only to screen readers. */
    showLabel?: boolean
    /** Slow halo for states that mean "in progress", e.g. a running job. */
    pulse?: boolean
  }>(),
  { tone: 'neutral' },
)
</script>

<template>
  <span class="e-status" :class="[`e-status--${tone}`, { 'e-status--pulse': pulse }]">
    <span class="e-status__dot" aria-hidden="true" />
    <span :class="showLabel ? 'e-status__label' : 'e-visually-hidden'">{{ label }}</span>
  </span>
</template>

<style>
.e-status {
  display: inline-flex;
  align-items: center;
  gap: var(--erpx-space-2);
  font-size: var(--erpx-text-sm);
  line-height: var(--erpx-leading-tight);
  white-space: nowrap;
}

.e-status__dot {
  position: relative;
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: var(--erpx-radius-full);
  background-color: currentColor;
}

.e-status__label {
  color: var(--erpx-color-text-secondary);
}

.e-status--neutral { color: var(--erpx-color-neutral-solid); }
.e-status--accent  { color: var(--erpx-color-accent-solid); }
.e-status--success { color: var(--erpx-color-success-solid); }
.e-status--warning { color: var(--erpx-color-warning-solid); }
.e-status--danger  { color: var(--erpx-color-danger-solid); }
.e-status--info    { color: var(--erpx-color-info-solid); }

.e-status--pulse .e-status__dot::after {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: inherit;
  background-color: currentColor;
  opacity: 0.35;
  animation: e-status-pulse 1.8s var(--erpx-easing-standard) infinite;
}

@keyframes e-status-pulse {
  0% { transform: scale(0.7); opacity: 0.35; }
  70% { transform: scale(1.4); opacity: 0; }
  100% { transform: scale(1.4); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .e-status--pulse .e-status__dot::after {
    animation: none;
  }
}

.e-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
</style>
